import { useCallback, useEffect, useRef, useState } from "react";
import type { VehicleTestImages } from "@/types/vehicleTest";
import {
  getSectionImageCount,
  getSectionMax,
  type PhotoSessionSection,
} from "@/components/car-post-ad/photoSession/photoSessionUtils";
import { psLog, psWarn } from "@/components/car-post-ad/photoSession/photoSessionDebug";
import { isVideoMedia } from "@/lib/isVideoMedia";

export type ListingPhotoQueueStatus =
  | "pending"
  | "uploading"
  | "done"
  | "failed";

export interface ListingPhotoQueueItem {
  id: string;
  sessionId: string;
  blob: Blob;
  previewUrl: string;
  status: ListingPhotoQueueStatus;
  error?: string;
  section: PhotoSessionSection;
  tireType?: "primary" | "secondary";
  isThumbnail?: boolean;
  /** 1-based position in section when captured (for guided retake). */
  photoIndex?: number;
  /** Image id in listing state after successful upload. */
  uploadedImageId?: string;
}

const MAX_CONCURRENT = 3;

type ProcessImageFileFn = (
  file: File,
  section: PhotoSessionSection,
  isThumbnail?: boolean,
  tireType?: "primary" | "secondary",
) => Promise<string>;

interface UseListingPhotoUploadQueueProps {
  currentImages: VehicleTestImages;
  processImageFile: ProcessImageFileFn;
  /** Called when an upload finishes after the queue item was already removed. */
  onOrphanedUpload?: (
    item: Pick<
      ListingPhotoQueueItem,
      "section" | "tireType" | "isThumbnail" | "photoIndex"
    >,
    uploadedImageId: string,
  ) => void;
}

export function useListingPhotoUploadQueue({
  currentImages,
  processImageFile,
  onOrphanedUpload,
}: UseListingPhotoUploadQueueProps) {
  const [queue, setQueue] = useState<ListingPhotoQueueItem[]>([]);
  const processingRef = useRef(false);
  // Only mutate via syncQueue so concurrent processQueue calls never see a
  // stale "pending" status after we've already marked an item uploading.
  const queueRef = useRef<ListingPhotoQueueItem[]>([]);
  const currentImagesRef = useRef(currentImages);
  currentImagesRef.current = currentImages;
  const onOrphanedUploadRef = useRef(onOrphanedUpload);
  useEffect(() => {
    onOrphanedUploadRef.current = onOrphanedUpload;
  }, [onOrphanedUpload]);

  const syncQueue = useCallback(
    (updater: (prev: ListingPhotoQueueItem[]) => ListingPhotoQueueItem[]) => {
      const next = updater(queueRef.current);
      queueRef.current = next;
      setQueue(next);
      return next;
    },
    [],
  );

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;

    const q = queueRef.current;
    const pending = q.filter((item) => item.status === "pending");
    const inProgress = q.filter((item) => item.status === "uploading");
    if (pending.length === 0 || inProgress.length >= MAX_CONCURRENT) return;

    processingRef.current = true;
    const next = pending[0];

    // Mark uploading on the ref immediately so a concurrent processQueue
    // (e.g. after processImageFile identity change) cannot pick the same item.
    syncQueue((prev) =>
      prev.map((item) =>
        item.id === next.id ? { ...item, status: "uploading" as const } : item,
      ),
    );

    try {
      const mime = (next.blob.type || "image/jpeg").toLowerCase();
      const ext = mime.includes("webm")
        ? "webm"
        : mime.includes("mp4")
          ? "mp4"
          : mime.includes("quicktime")
            ? "mov"
            : mime.includes("png")
              ? "png"
              : "jpg";
      const file = new File(
        [next.blob],
        `${mime.startsWith("video/") ? "video" : "photo"}_${next.id}.${ext}`,
        { type: next.blob.type || "image/jpeg" },
      );

      const uploadedImageId = await processImageFile(
        file,
        next.section,
        next.isThumbnail,
        next.tireType,
      );

      // processImageFile resolves undefined when it rejected the file (type/size
      // validation toasts + returns). Never mark that as "done" – the media was not uploaded.
      if (!uploadedImageId) {
        throw new Error("upload_rejected");
      }

      syncQueue((prev) => {
        const stillQueued = prev.some((item) => item.id === next.id);
        if (!stillQueued) {
          if (uploadedImageId) {
            psLog("queue.orphanedUpload", { id: next.id, uploadedImageId });
            queueMicrotask(() => {
              onOrphanedUploadRef.current?.(next, uploadedImageId);
            });
          }
          return prev;
        }
        return prev.map((item) =>
          item.id === next.id
            ? {
                ...item,
                status: "done" as const,
                uploadedImageId: uploadedImageId || item.uploadedImageId,
              }
            : item,
        );
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      syncQueue((prev) => {
        if (!prev.some((item) => item.id === next.id)) return prev;
        return prev.map((item) =>
          item.id === next.id
            ? { ...item, status: "failed" as const, error: msg }
            : item,
        );
      });
    } finally {
      processingRef.current = false;
      setTimeout(() => processQueue(), 0);
    }
  }, [processImageFile, syncQueue]);

  const triggerProcess = useCallback(() => {
    setTimeout(() => processQueue(), 50);
  }, [processQueue]);

  const resolveIsThumbnail = useCallback(
    (
      section: PhotoSessionSection,
      tireType?: "primary" | "secondary",
    ): boolean => {
      if (section !== "exterior") return false;
      const images = currentImagesRef.current;
      const q = queueRef.current;
      const uploaded = getSectionImageCount(images, section, tireType);
      const inFlight = q.filter(
        (item) =>
          item.section === section &&
          item.tireType === tireType &&
          (item.status === "pending" || item.status === "uploading"),
      ).length;
      return uploaded + inFlight === 0;
    },
    [],
  );

  const enqueue = useCallback(
    (
      sessionId: string,
      section: PhotoSessionSection,
      blob: Blob,
      tireType?: "primary" | "secondary",
    ): string | null => {
      const isVideo = (blob.type || "").toLowerCase().startsWith("video/");
      const images = currentImagesRef.current;
      const occupied = queueRef.current.filter(
        (item) =>
          item.section === section &&
          item.tireType === tireType &&
          (item.status === "pending" ||
            item.status === "uploading" ||
            item.status === "done"),
      ).length;
      const uploaded = getSectionImageCount(images, section, tireType);

      if (isVideo) {
        const hasEngine = (images.exterior || []).some((img) =>
          isVideoMedia(img.type, img.url),
        );
        // "done" only blocks if that upload is still present in exterior state.
        // Otherwise removing via the red X left an orphan queue row and blocked re-record.
        const videoInFlight = queueRef.current.some((item) => {
          if (item.section !== section) return false;
          if (!(item.blob.type || "").toLowerCase().startsWith("video/")) {
            return false;
          }
          if (item.status === "pending" || item.status === "uploading") {
            return true;
          }
          if (item.status === "done") {
            if (!item.uploadedImageId) return true;
            return (images.exterior || []).some(
              (img) => img.id === item.uploadedImageId,
            );
          }
          return false;
        });
        if (section === "exterior" && (hasEngine || videoInFlight)) {
          psWarn("queue.enqueue.rejected", { reason: "engine_video_exists" });
          return null;
        }
      } else {
        const max = getSectionMax(section);
        if (Math.max(uploaded, occupied) >= max) {
          psWarn("queue.enqueue.rejected", {
            reason: "max_reached",
            uploaded,
            occupied,
            max,
          });
          return null;
        }
      }

      const isThumbnail =
        resolveIsThumbnail(section, tireType) && !isVideo;
      const inFlight = queueRef.current.filter(
        (item) =>
          item.section === section &&
          item.tireType === tireType &&
          (item.status === "pending" || item.status === "uploading"),
      ).length;
      const photoIndex =
        getSectionImageCount(images, section, tireType) + inFlight + 1;
      const id = `photo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const previewUrl = URL.createObjectURL(blob);
      psLog("queue.enqueue", {
        id,
        sessionId,
        section,
        tireType,
        blobSize: blob.size,
        isThumbnail,
      });
      const item: ListingPhotoQueueItem = {
        id,
        sessionId,
        blob,
        previewUrl,
        status: "pending",
        section,
        tireType,
        isThumbnail,
        photoIndex,
      };
      syncQueue((prev) => [...prev, item]);
      triggerProcess();
      return id;
    },
    [resolveIsThumbnail, triggerProcess, syncQueue],
  );

  const remove = useCallback(
    (id: string) => {
      syncQueue((prev) => {
        const item = prev.find((q) => q.id === id);
        if (
          !item ||
          (item.status !== "pending" &&
            item.status !== "uploading" &&
            item.status !== "failed" &&
            item.status !== "done")
        ) {
          return prev;
        }
        URL.revokeObjectURL(item.previewUrl);
        return prev.filter((q) => q.id !== id);
      });
    },
    [syncQueue],
  );

  /** Drop queue rows that point at an image already removed from listing UI/API. */
  const removeByUploadedImageId = useCallback(
    (uploadedImageId: string) => {
      if (!uploadedImageId) return;
      syncQueue((prev) => {
        const next = prev.filter((q) => {
          if (q.uploadedImageId !== uploadedImageId) return true;
          URL.revokeObjectURL(q.previewUrl);
          return false;
        });
        return next.length === prev.length ? prev : next;
      });
    },
    [syncQueue],
  );

  /** Replace blob for a pending/failed/done item (retake same slot in session). */
  const replacePending = useCallback(
    (id: string, blob: Blob): boolean => {
      let replaced = false;
      syncQueue((prev) => {
        const item = prev.find((q) => q.id === id);
        if (
          !item ||
          (item.status !== "pending" &&
            item.status !== "failed" &&
            item.status !== "done")
        ) {
          return prev;
        }
        URL.revokeObjectURL(item.previewUrl);
        const previewUrl = URL.createObjectURL(blob);
        replaced = true;
        psLog("queue.replacePending", { id, blobSize: blob.size });
        return prev.map((q) =>
          q.id === id
            ? {
                ...q,
                blob,
                previewUrl,
                status: "pending" as const,
                error: undefined,
                uploadedImageId: undefined,
              }
            : q,
        );
      });
      if (replaced) triggerProcess();
      return replaced;
    },
    [triggerProcess, syncQueue],
  );

  const retry = useCallback(
    (id: string) => {
      syncQueue((prev) =>
        prev.map((q) =>
          q.id === id
            ? { ...q, status: "pending" as const, error: undefined }
            : q,
        ),
      );
      triggerProcess();
    },
    [triggerProcess, syncQueue],
  );

  const getSessionItems = useCallback(
    (sessionId: string) =>
      queue.filter((item) => item.sessionId === sessionId),
    [queue],
  );

  const countInFlightForSection = useCallback(
    (
      section: PhotoSessionSection,
      tireType?: "primary" | "secondary",
    ) => {
      return queue.filter(
        (item) =>
          item.section === section &&
          item.tireType === tireType &&
          (item.status === "pending" || item.status === "uploading"),
      ).length;
    },
    [queue],
  );

  return {
    queue,
    enqueue,
    remove,
    removeByUploadedImageId,
    replacePending,
    retry,
    getSessionItems,
    countInFlightForSection,
  };
}
